import { is } from "rambda";
import { MondayIntegrationType } from "shared/integrations/integration";
import { z } from "zod";

const defaultText = "Unset";
const defaultColor = "#797e93";
const unsetStatusId = "5";
const defaultStatus = { text: defaultText, color: defaultColor };

const parsedStatusSchema = z.object({
  text: z.string(),
  color: z.string(),
});

export function getStatuses(column: any) {
  if (!column) {
    return [];
  }

  const settings = column.settings ?? (column.settings_str ? JSON.parse(column.settings_str) : {});
  const labels: Record<string, string> = settings?.labels;
  const positions: Record<string, number> = settings?.labels_positions_v2;

  if (!labels) {
    return [];
  }

  // if the default status is not in the labels, add it
  if (!labels[unsetStatusId]) {
    labels[unsetStatusId] = defaultText;
  }

  let orderedStatusIndexes: [string, any][] = [];
  if (positions) {
    orderedStatusIndexes = Object.entries(labels).sort(([id1], [id2]) => positions[id1] - positions[id2]);
  } else {
    orderedStatusIndexes = Object.entries(labels);
  }
  const statuses = orderedStatusIndexes.map(([labelId]) => {
    const { color = defaultColor } = settings?.labels_colors?.[labelId] ?? {};
    return {
      id: labelId,
      title: settings?.labels[labelId] ?? defaultText,
      description: column.description ?? "",
      color,
    };
  });
  return statuses;
}

export const getDropDownOptions = (column: any): { id: number; title: string }[] => {
  const settings = column.settings;

  return settings?.labels.map((option: any) => {
    return {
      id: option.id,
      title: option.name,
    };
  });
};

export function parseStatusColumn(colAndValue: any, columnDefinition: any): { text: string; color: string } {
  if (colAndValue && is(Object, colAndValue) && columnDefinition) {
    const indexAsStr = colAndValue.index?.toString();

    if (indexAsStr === unsetStatusId) {
      return defaultStatus;
    }

    if (!Number.isNaN(+colAndValue.index)) {
      const settings =
        columnDefinition.settings ?? (columnDefinition.settings_str ? JSON.parse(columnDefinition.settings_str) : {});

      if (settings && settings.labels) {
        return {
          text: settings.labels[indexAsStr],
          color: settings.labels_colors?.[indexAsStr]?.color ?? defaultColor,
        };
      }
    }
    if (parsedStatusSchema.safeParse(colAndValue).success) {
      return colAndValue;
    }
  }
  return defaultStatus;
}

export function getSelectedStatus(columnValues: any, column: any): { text: string; color: string } {
  let status = columnValues?.find((columnValue: any) => columnValue.id === column?.id)?.value;
  return parseStatusColumn(status, column);
}

export function getDefaultColumnsMapping(columns: any[], subType: MondayIntegrationType) {
  const selectedColumns = [];
  switch (subType) {
    case MondayIntegrationType.tasks:
    case MondayIntegrationType.live: {
      const statusColumn = columns.find((c) => c.type === "status");
      const dateColumn = columns.find((c) => c.type === "date");
      const peopleColumn = columns.find((c) => c.type === "multiple-person");
      const selectedColumns = [];
      if (statusColumn) {
        selectedColumns.push(statusColumn);
      }
      if (dateColumn) {
        selectedColumns.push(dateColumn);
      }
      if (peopleColumn) {
        selectedColumns.push(peopleColumn);
      }
      return selectedColumns;
    }
    case MondayIntegrationType.org_chart: {
      {
        const parentColumn = columns.find((c) => c.title === "parent");
        const selfColumn = columns.find((c) => c.title === "self");
        const jobTitleColumn = columns.find((c) => c.title === "position");
        if (parentColumn) {
          selectedColumns.push(parentColumn);
        }
        if (selfColumn) {
          selectedColumns.push(selfColumn);
        }
        if (jobTitleColumn) {
          selectedColumns.push(jobTitleColumn);
        }
      }
      return selectedColumns;
    }
  }
}
